import React, { useState } from 'react';
import { AritoDialog, AritoIcon } from '@/components/custom/arito';
import { SearchFieldInput } from '../search-field-input';
import { useDialogState, useSelection } from './hooks';
import { ActionsButton } from './ActionsButton';
import { DataTable } from './DataTable';
import { cn } from '@/lib/utils';

// Type overload để hỗ trợ backward compatibility
interface SearchFieldPropsWithNull<T = any> {
  name?: string;
  type?: string;
  disabled?: boolean;
  className?: string;
  columnDisplay?: keyof T;
  rows?: T[];
  searchEndpoint?: string;
  otherSearchEndpoint?: string;
  searchColumns?: any[];
  displayRelatedField?: keyof T;
  classNameRelatedField?: string;
  relatedFieldValue?: string;
  dialogTitle?: string;
  hideRowNumberColumn?: boolean;
  headerFields?: React.ReactNode;
  placeholder?: string;
  value?: any;
  onValueChange?: (value: any) => void;
  onRowSelection?: (row: T | null) => void;
  onBeforeOpen?: () => void | Promise<void>;
}

interface SearchFieldPropsWithoutNull<T = any> {
  name?: string;
  type?: string;
  disabled?: boolean;
  className?: string;
  columnDisplay?: keyof T;
  rows?: T[];
  searchEndpoint?: string;
  otherSearchEndpoint?: string;
  searchColumns?: any[];
  displayRelatedField?: keyof T;
  classNameRelatedField?: string;
  relatedFieldValue?: string;
  dialogTitle?: string;
  hideRowNumberColumn?: boolean;
  headerFields?: React.ReactNode;
  placeholder?: string;
  value?: any;
  onValueChange?: (value: any) => void;
  onRowSelection?: (row: T) => void;
  onBeforeOpen?: () => void | Promise<void>;
}

type SearchFieldProps<T = any> = SearchFieldPropsWithNull<T> | SearchFieldPropsWithoutNull<T>;

export const SearchFieldV2 = <T = any,>({
  type = 'text',
  value = '',
  searchEndpoint,
  searchColumns = [],
  className,
  dialogTitle = 'Danh mục',
  placeholder,
  onRowSelection,
  onValueChange,
  displayRelatedField,
  classNameRelatedField,
  relatedFieldValue,
  disabled,
  columnDisplay = '' as keyof T,
  rows,
  otherSearchEndpoint,
  onBeforeOpen
}: SearchFieldProps<T>) => {
  // Wrapper function để tự động convert type
  const handleRowSelection = (row: T | null) => {
    if (onRowSelection) {
      // Type assertion để bypass TypeScript checking
      (onRowSelection as any)(row);
    }
  };
  const { searchDialogOpen, setSearchDialogOpen, isFullScreen, handleSearchClick, toggleFullScreen } = useDialogState();
  const { selectedObj, setSelectedObj, clearSelection } = useSelection<T>();
  const [localSelectedObj, setLocalSelectedObj] = useState<T | null>(null);

  return (
    <div className={cn('relative flex items-center', className)}>
      <SearchFieldInput
        type={type}
        disabled={disabled}
        className={cn('w-40', className)}
        value={String(value || selectedObj?.[columnDisplay] || '')}
        onChange={event => onValueChange?.(event)}
        placeholder={placeholder}
        onSearchClick={async event => {
          event.preventDefault();
          event.stopPropagation();
          try {
            await onBeforeOpen?.();
          } catch {
            // Ignore pre-open errors to not block opening dialog
          }
          handleSearchClick();
        }}
      />

      {displayRelatedField && (
        <div
          className={cn(
            'ml-0 mt-1 flex-1 text-ellipsis whitespace-nowrap text-left text-sm text-gray-700 sm:ml-3 sm:mt-0',
            classNameRelatedField
          )}
        >
          {relatedFieldValue || (selectedObj?.[displayRelatedField] as string)}
        </div>
      )}

      <AritoDialog
        open={searchDialogOpen}
        onClose={() => {}}
        disableBackdropClose={true}
        disableEscapeKeyDown={false}
        fullScreen={isFullScreen}
        onFullscreenToggle={toggleFullScreen}
        title={dialogTitle}
        titleIcon={<AritoIcon icon={584} className='mx-2' />}
        actions={
          <ActionsButton
            selectedSearchResult={localSelectedObj}
            onConfirm={() => {
              if (localSelectedObj) {
                setSelectedObj(localSelectedObj);
                handleRowSelection(localSelectedObj);
                setSearchDialogOpen(false);
              } else {
                handleRowSelection(null);
                setSearchDialogOpen(false);
              }
            }}
            onCancel={() => {
              clearSelection();
              setLocalSelectedObj(null);
              handleRowSelection(null);
              setSearchDialogOpen(false);
            }}
          />
        }
        classNameContent='max-h-[400px] w-[850px]'
      >
        <DataTable
          searchEndpoint={searchEndpoint}
          otherSearchEndpoint={otherSearchEndpoint}
          searchColumns={searchColumns}
          columnDisplay={columnDisplay}
          rows={rows}
          onRowSelection={setLocalSelectedObj}
          onSelectedObjChange={obj => obj && setLocalSelectedObj(obj)}
        />
      </AritoDialog>
    </div>
  );
};
